/**
 * Role Permissions matrix (Bug bundle #8 — 2026-08-11)
 *
 * Rewrite of the Phase 19 role-list page. This is now a full roles ×
 * permissions grid with:
 *   - Category filter (chips at the top).
 *   - Text search across permission keys.
 *   - Click a cell to toggle a single (role, permission) — PATCHes
 *     /api/permissions/role/{role}/{key} with {value}.
 *   - Every toggle writes an audit_logs entry (backend does this).
 */
import { useEffect, useMemo, useState, Fragment } from "react";
import { toast } from "sonner";
import {
  Loader2, Search, ShieldCheck, X, Check, ArrowLeft,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";

const ROLE_SHORT_LABELS = {
  system_admin: "Sys Admin",
  somturk_data_entry: "Data Entry",
  somturk_observer: "Observer",
  salihi_accountant: "Junior",
  salihi_senior_accountant: "Senior",
  salihi_finance_manager: "Fin Mgr",
  salihi_compliance_officer: "Compliance",
  internal_auditor: "Auditor",
};

export default function RolePermissionsPage() {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState("all");
  const [search, setSearch] = useState("");
  const [savingCell, setSavingCell] = useState(null); // "role:key"

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/permissions/matrix");
      setMatrix(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const roles = matrix?.roles || [];
  const categories = matrix?.categories || [];

  const visibleKeys = useMemo(() => {
    if (!matrix) return [];
    const q = search.trim().toLowerCase();
    let keys = matrix.permission_keys || [];
    if (selectedCat !== "all") {
      const c = categories.find((c) => c.prefix === selectedCat);
      keys = c ? c.keys : [];
    }
    if (q) keys = keys.filter((k) => k.toLowerCase().includes(q));
    return keys;
  }, [matrix, categories, selectedCat, search]);

  const grouped = useMemo(() => {
    if (!matrix) return [];
    const catByPrefix = {};
    categories.forEach((c) => { catByPrefix[c.prefix] = c; });
    const perPrefix = new Map();
    for (const k of visibleKeys) {
      const cat = categories.find((c) => c.keys.includes(k));
      const p = cat ? cat.prefix : "other";
      if (!perPrefix.has(p)) perPrefix.set(p, []);
      perPrefix.get(p).push(k);
    }
    return Array.from(perPrefix.entries()).map(([prefix, keys]) => ({
      prefix,
      label: catByPrefix[prefix]?.label || "Other",
      keys,
    }));
  }, [matrix, categories, visibleKeys]);

  const toggle = async (role, key, newVal) => {
    const cellId = `${role}:${key}`;
    setSavingCell(cellId);
    setMatrix((prev) => {
      if (!prev) return prev;
      const nextRoles = prev.roles.map((r) =>
        r.role === role
          ? { ...r, permissions: { ...r.permissions, [key]: newVal } }
          : r
      );
      return { ...prev, roles: nextRoles };
    });
    try {
      await api.patch(
        `/permissions/role/${role}/${encodeURIComponent(key)}`,
        { value: newVal }
      );
    } catch (e) {
      toast.error(formatApiError(e));
      setMatrix((prev) => {
        if (!prev) return prev;
        const nextRoles = prev.roles.map((r) =>
          r.role === role
            ? { ...r, permissions: { ...r.permissions, [key]: !newVal } }
            : r
        );
        return { ...prev, roles: nextRoles };
      });
    } finally {
      setSavingCell(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="role-permissions-page">
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-2xl text-[#2F86B5] flex items-center gap-2">
                <ShieldCheck className="h-6 w-6" />
                Role Permissions Matrix
              </CardTitle>
              <CardDescription>
                {matrix
                  ? `${matrix.permission_keys.length} permissions × ${matrix.roles.length} roles. Click a cell to toggle.`
                  : "Loading…"}
              </CardDescription>
            </div>
            <a href="/settings" className="text-xs text-slate-500 hover:underline flex items-center gap-1">
              <ArrowLeft className="h-3 w-3" /> Settings
            </a>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search permissions… (e.g. loan, payroll, verify)"
                className="pl-8 h-9"
                data-testid="perm-search"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-2.5 text-slate-400 hover:text-slate-700"
                  aria-label="Clear search"
                ><X className="h-4 w-4" /></button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5" data-testid="cat-filter-bar">
              <CatChip active={selectedCat === "all"} onClick={() => setSelectedCat("all")}
                       label="All" count={matrix?.permission_keys?.length}
                       data-testid="cat-chip-all"/>
              {categories.map((c) => (
                <CatChip
                  key={c.prefix}
                  active={selectedCat === c.prefix}
                  onClick={() => setSelectedCat(c.prefix)}
                  label={c.label}
                  count={c.keys.length}
                  data-testid={`cat-chip-${c.prefix}`}
                />
              ))}
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          {loading || !matrix ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-[#4FA3D1]" />
            </div>
          ) : visibleKeys.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm">
              No permissions match your search.
            </div>
          ) : (
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <table className="min-w-full text-xs" data-testid="perm-matrix-table">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-700 border-r border-slate-200 sticky left-0 bg-slate-50 min-w-[280px]">
                      Permission
                    </th>
                    {roles.map((r) => (
                      <th key={r.role} className="px-2 py-2.5 font-semibold text-slate-700 text-center min-w-[92px]">
                        <div className="whitespace-nowrap">{ROLE_SHORT_LABELS[r.role] || r.role}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grouped.map((g) => (
                    <Fragment key={`cat-${g.prefix}`}>
                      <tr className="bg-[#EEF6FA]">
                        <td colSpan={roles.length + 1} className="px-3 py-1.5 font-semibold text-[#2F86B5] text-[11px] uppercase tracking-wide">
                          {g.label} — {g.keys.length}
                        </td>
                      </tr>
                      {g.keys.map((k, i) => (
                        <tr key={k} className={i % 2 ? "bg-white" : "bg-slate-50/40"} data-testid={`perm-row-${k}`}>
                          <td className="px-3 py-1.5 font-mono text-[11px] text-slate-700 border-r border-slate-100 sticky left-0 bg-inherit whitespace-nowrap">
                            {k}
                          </td>
                          {roles.map((r) => {
                            const on = !!r.permissions[k];
                            const cellId = `${r.role}:${k}`;
                            const saving = savingCell === cellId;
                            return (
                              <td key={r.role} className="px-2 py-1 text-center border-l border-slate-100">
                                <button
                                  type="button"
                                  className={
                                    "inline-flex items-center justify-center w-6 h-6 rounded transition " +
                                    (on
                                      ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                                      : "bg-slate-200 hover:bg-slate-300 text-slate-400")
                                  }
                                  onClick={() => !saving && toggle(r.role, k, !on)}
                                  disabled={saving}
                                  title={`${r.role} · ${k} → ${on ? "granted" : "denied"} (click to toggle)`}
                                  data-testid={`cell-${r.role}-${k}`}
                                >
                                  {saving ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : on ? (
                                    <Check className="h-3.5 w-3.5" />
                                  ) : (
                                    <X className="h-3 w-3" />
                                  )}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-slate-500">
        Every toggle is recorded to the audit log with actor email, target role,
        permission key and old→new value. Per-user overrides are managed from
        the Users page → Permissions.
      </p>
    </div>
  );
}

function CatChip({ active, onClick, label, count, ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "px-2.5 py-1 rounded-full text-[11px] font-medium transition " +
        (active
          ? "bg-[#4FA3D1] text-white shadow-sm"
          : "bg-slate-100 text-slate-600 hover:bg-slate-200")
      }
      {...rest}
    >
      {label}
      {count != null && (
        <Badge
          className={
            "ml-1.5 px-1.5 h-4 text-[9px] " +
            (active
              ? "bg-white/20 text-white hover:bg-white/20"
              : "bg-slate-300 text-slate-700 hover:bg-slate-300")
          }
        >
          {count}
        </Badge>
      )}
    </button>
  );
}
